import React, { useState } from "react";
import { Phone, Mail, MapPin, Clock, FileText, Shield } from "lucide-react";
import { COMPANY } from "@/data/feniks";
import PrivacyPolicyModal from "@/components/PrivacyPolicyModal";

export default function Footer() {
  const [showPrivacy, setShowPrivacy] = useState(false);
  return (
    <footer
      data-testid="footer"
      className="relative bg-zinc-950 text-zinc-300 pt-20 pb-10 overflow-hidden"
    >
      <PrivacyPolicyModal open={showPrivacy} onClose={() => setShowPrivacy(false)} />
      <div className="absolute inset-0 blueprint-grid opacity-50 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-5 md:px-8">
        <div className="grid md:grid-cols-12 gap-10 mb-12">
          {/* Logo + dane firmowe */}
          <div className="md:col-span-5">
            <img
              src={COMPANY.logo.light}
              alt="Feniks Gastro & AGD logo"
              className="h-20 md:h-24 w-auto object-contain mb-5"
            />

            <div
              data-testid="footer-company-info"
              className="border-l-2 border-[var(--feniks-red)] pl-4 mb-5"
            >
              <div className="font-display text-base md:text-lg tracking-tight text-white mb-1">
                {COMPANY.name}
              </div>
              <div className="text-sm text-zinc-400 leading-snug">
                ul. Tyska 2
                <br />
                40-655 Katowice
              </div>
              <div className="mt-2 flex items-center gap-2 text-[11px] font-mono tracking-wider text-zinc-500">
                <FileText size={12} className="text-[var(--feniks-red)]" />
                NIP: {COMPANY.nip}
              </div>
            </div>

            <p className="text-sm leading-relaxed text-zinc-400 max-w-sm">
              Profesjonalny serwis sprzętu AGD i gastronomicznego. Górny Śląsk i Zagłębie.
              Naprawiamy zamiast wyrzucać.
            </p>
          </div>

          {/* Kontakt */}
          <div className="md:col-span-3">
            <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-zinc-500 mb-4">
              Kontakt
            </div>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-2">
                <Phone size={14} className="text-[var(--feniks-red)] mt-1 shrink-0" />
                <a
                  data-testid="footer-phone"
                  href={`tel:${COMPANY.phoneRaw}`}
                  className="hover:text-white transition-colors font-semibold"
                >
                  {COMPANY.phone}
                </a>
              </li>
              <li className="flex items-start gap-2">
                <Mail size={14} className="text-[var(--feniks-red)] mt-1 shrink-0" />
                <a
                  href={`mailto:${COMPANY.email}`}
                  className="hover:text-white transition-colors break-all"
                >
                  {COMPANY.email}
                </a>
              </li>
              <li className="flex items-start gap-2">
                <MapPin size={14} className="text-[var(--feniks-red)] mt-1 shrink-0" />
                <span>{COMPANY.address}</span>
              </li>
            </ul>
          </div>

          {/* Godziny pracy */}
          <div className="md:col-span-2">
            <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-zinc-500 mb-4 flex items-center gap-1.5">
              <Clock size={12} /> Godziny
            </div>
            <ul data-testid="footer-hours" className="space-y-2 text-xs">
              {COMPANY.hoursLines.map((h, i) => (
                <li
                  key={i}
                  className="flex justify-between gap-2 border-b border-zinc-900 pb-1.5"
                >
                  <span className="text-zinc-400">{h.day}</span>
                  <span
                    className={`font-mono font-semibold ${
                      h.time === "Nieczynne" ? "text-zinc-600" : "text-white"
                    }`}
                  >
                    {h.time}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Nawigacja */}
          <div className="md:col-span-2">
            <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-zinc-500 mb-4">
              Nawigacja
            </div>
            <ul className="flex flex-col gap-2.5 text-sm">
              {[
                ["o-nas", "O nas"],
                ["uslugi", "Usługi"],
                ["cennik", "Cennik"],
                ["obszar", "Obszar działania"],
                ["kontakt", "Kontakt"],
              ].map(([id, label]) => (
                <li key={id}>
                  <a
                    href={`#${id}`}
                    className="text-zinc-400 hover:text-white transition-colors uppercase tracking-wider text-xs"
                  >
                    {label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Massive FENIKS text */}
        <div className="border-t border-zinc-800 pt-10 mb-8 overflow-hidden">
          <div className="font-display text-[16vw] md:text-[12vw] leading-none tracking-tighter text-white/[0.04] select-none whitespace-nowrap">
            FENIKS GASTRO {"&"} AGD
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs text-zinc-500 font-mono">
          <div>
            © {new Date().getFullYear()} {COMPANY.name} · NIP {COMPANY.nip}. Wszelkie prawa zastrzeżone.
          </div>
          <div className="flex items-center gap-4">
            <button
              data-testid="footer-privacy-link"
              onClick={() => setShowPrivacy(true)}
              className="inline-flex items-center gap-1.5 hover:text-white transition-colors"
            >
              <Shield size={11} /> Polityka prywatności
            </button>
            <span className="hidden sm:inline">·</span>
            <span>Śląsk, Polska</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
